import { CircleHelp, GraduationCap, LogOut, Moon, Shield, Sun, User } from 'lucide-react';
import {
  Button,
  Chip,
  Dropdown,
  Header,
  Label,
  Modal,
  Switch,
  useOverlayState,
} from '@heroui/react';
import type { Key } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doSignOut } from '../../lib/firebase';
import { useOnboardingTour } from '../../features/onboarding/onboarding-tour-provider';
import { ReportProblemDrawer } from '../../features/support/components/report-problem-drawer';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentCondominium } from '../../hooks/useCurrentCondominium';
import { useThemeStore } from '../../stores/themeStore';
import { AsyncButton } from './async-button';
import { UserAvatar } from './user-avatar';

interface UserMenuDropdownProps {
  displayName: string;
  email?: string | null;
  photoUrl?: string | null;
  onOpenSwitchCondominium?: () => void;
  canOpenSwitchCondominium?: boolean;
}

export function UserMenuDropdown({
  displayName,
  email,
  photoUrl,
  onOpenSwitchCondominium,
  canOpenSwitchCondominium = false,
}: UserMenuDropdownProps) {
  const navigate = useNavigate();
  const { authUser, userDoc } = useAuth();
  const tour = useOnboardingTour();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const isDark = theme === 'dark';
  const { label: condominiumLabel, isLoading: isCondominiumLoading } = useCurrentCondominium();
  const isGlobalAdmin = userDoc?.isGlobalAdmin ?? false;
  const isCondominiumManager = userDoc?.isCondominiumManager ?? false;
  const logoutModal = useOverlayState();
  const reportProblemDrawer = useOverlayState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  const openSwitchCondominiumModal = () => {
    closeMenu();
    requestAnimationFrame(() => onOpenSwitchCondominium?.());
  };

  const openLogoutModal = () => {
    closeMenu();
    requestAnimationFrame(() => logoutModal.open());
  };

  const openReportProblemDrawer = () => {
    closeMenu();
    requestAnimationFrame(() => reportProblemDrawer.open());
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await doSignOut();
      logoutModal.close();
      navigate('/login');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleAction = (key: Key) => {
    if (key === 'edit-profile') {
      closeMenu();
      navigate('/edit-profile');
      return;
    }

    if (key === 'privacy') {
      closeMenu();
      navigate('/privacidade');
      return;
    }

    if (key === 'help') {
      openReportProblemDrawer();
      return;
    }

    if (key === 'replay-tutorial') {
      closeMenu();
      // O menu precisa fechar antes, senão o overlay do tour abre atrás dele.
      requestAnimationFrame(() => tour?.replayTour());
      return;
    }

    if (key === 'logout') {
      openLogoutModal();
    }
  };

  return (
    <>
      <Dropdown isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <Dropdown.Trigger
          aria-label="Menu do usuário"
          data-tour="topbar-user-menu"
          className="flex max-w-[12rem] items-center gap-2 rounded-lg px-1 py-1"
        >
          <div className="hidden min-w-0 flex-1 text-right md:block">
            <p className="truncate text-sm font-medium leading-tight text-foreground">{displayName}</p>
            <p className="truncate text-xs leading-tight text-muted">
              {isCondominiumLoading ? 'Carregando…' : (condominiumLabel ?? '—')}
            </p>
          </div>
          <UserAvatar name={displayName} photoUrl={photoUrl} size="md" className="shrink-0" />
        </Dropdown.Trigger>
        <Dropdown.Popover
          placement="bottom end"
          className="min-w-56 overflow-hidden border border-separator bg-surface shadow-lg"
        >
          <div className="flex items-center gap-3 border-b border-separator px-4 py-4">
            <UserAvatar
              name={displayName}
              photoUrl={photoUrl}
              size="lg"
              className="!size-14 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{displayName}</p>
              {email ? <p className="truncate text-xs text-muted">{email}</p> : null}
              {isGlobalAdmin || isCondominiumManager ? (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {isGlobalAdmin ? (
                    <Chip size="sm" variant="soft" color="warning">
                      Admin Global
                    </Chip>
                  ) : null}
                  {isCondominiumManager ? (
                    <Chip size="sm" variant="soft" color="accent">
                      Gestor de Condomínio
                    </Chip>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="border-b border-separator px-3 py-3">
            <p className="mb-2 px-1 text-xs font-medium text-muted">Condomínio</p>
            <div className="flex items-center justify-between gap-3 px-1">
              <span className="min-w-0 truncate text-sm text-foreground">
                {isCondominiumLoading ? 'Carregando…' : (condominiumLabel ?? '—')}
              </span>
              {canOpenSwitchCondominium ? (
                <Button
                  size="sm"
                  variant="secondary"
                  isDisabled={isCondominiumLoading}
                  onPress={openSwitchCondominiumModal}
                >
                  Trocar
                </Button>
              ) : null}
            </div>
          </div>

          <div className="border-b border-separator px-3 py-3">
            <p className="mb-2 px-1 text-xs font-medium text-muted">Aparência</p>
            <div className="flex items-center justify-between gap-3 px-1">
              <span className="text-sm text-foreground">Modo escuro</span>
              <Switch
                aria-label="Modo escuro"
                isSelected={isDark}
                size="lg"
                onChange={(selected) => setTheme(selected ? 'dark' : 'light')}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb>
                      <Switch.Icon>
                        {isDark ? (
                          <Sun className="size-4 text-inherit opacity-100" />
                        ) : (
                          <Moon className="size-4 text-inherit opacity-70" />
                        )}
                      </Switch.Icon>
                    </Switch.Thumb>
                  </Switch.Control>
                </Switch.Content>
              </Switch>
            </div>
          </div>

          <Dropdown.Menu onAction={(key) => handleAction(key)}>
            <Dropdown.Section>
              <Header>Ajuda</Header>
              {tour ? (
                <Dropdown.Item id="replay-tutorial" textValue="Ver tutorial">
                  <GraduationCap className="size-4 shrink-0 text-muted" />
                  <Label>Ver tutorial</Label>
                </Dropdown.Item>
              ) : null}
              <Dropdown.Item id="privacy" textValue="Política de Privacidade">
                <Shield className="size-4 shrink-0 text-muted" />
                <Label>Política de Privacidade</Label>
              </Dropdown.Item>
              <Dropdown.Item id="help" textValue="Ajuda / Reportar problema">
                <CircleHelp className="size-4 shrink-0 text-muted" />
                <Label>Ajuda / Reportar problema</Label>
              </Dropdown.Item>
            </Dropdown.Section>
            <Dropdown.Section>
              <Header>Conta</Header>
              <Dropdown.Item id="edit-profile" textValue="Perfil e conta">
                <User className="size-4 shrink-0 text-muted" />
                <Label>Perfil e conta</Label>
              </Dropdown.Item>
              <Dropdown.Item id="logout" textValue="Sair" variant="danger">
                <LogOut className="size-4 shrink-0 text-danger" />
                <Label>Sair</Label>
              </Dropdown.Item>
            </Dropdown.Section>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>

      <Modal state={logoutModal}>
        <Modal.Backdrop isDismissable className="z-[200]">
          <Modal.Container placement="center" className="z-[200]">
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Heading>Sair da conta</Modal.Heading>
                <Modal.CloseTrigger />
              </Modal.Header>
              <Modal.Body>
                <p className="text-muted">Tem certeza que deseja sair?</p>
              </Modal.Body>
              <Modal.Footer className="flex justify-end gap-2">
                <Button variant="secondary" onPress={() => logoutModal.close()}>
                  Cancelar
                </Button>
                <AsyncButton
                  variant="danger"
                  isPending={loggingOut}
                  onPress={() => void handleLogout()}
                >
                  Sair
                </AsyncButton>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <ReportProblemDrawer
        state={reportProblemDrawer}
        userUid={authUser?.uid}
        userEmail={email ?? authUser?.email}
      />
    </>
  );
}
